<template>
  <div class="dashboard-container">
    <!-- 导航栏 -->
    <nav class="navbar">
      <div class="nav-content">
        <h1 class="nav-title">🎵 TikTok 用户面板</h1>
        <button @click="handleLogout" class="logout-btn">
          退出登录
        </button>
      </div>
    </nav>

    <!-- 主要内容 -->
    <main class="main-content">
      <div class="content-wrapper">
        <!-- 用户信息卡片 -->
        <div class="user-card">
          <div class="user-header">
            <img 
              v-if="getters.userAvatar.value" 
              :src="getters.userAvatar.value" 
              :alt="getters.userDisplayName.value"
              class="user-avatar"
            >
            <div v-else class="user-avatar-placeholder">
              👤
            </div>
            <div class="user-details">
              <h2 class="user-name">{{ getters.userDisplayName.value }}</h2>
              <p class="user-id">Open ID: {{ state.user?.open_id }}</p>
              <p v-if="state.user?.union_id" class="user-union-id">
                Union ID: {{ state.user.union_id }}
              </p>
            </div>
          </div>
          
          <div class="user-actions">
            <button @click="refreshUserInfo" :disabled="state.loading" class="btn btn-primary">
              <span v-if="state.loading">🔄</span>
              <span v-else>🔄</span>
              {{ state.loading ? '刷新中...' : '刷新信息' }}
            </button>
            <button @click="refreshToken" :disabled="state.loading" class="btn btn-outline">
              <span>🔑</span>
              刷新令牌
            </button>
          </div>
        </div>

        <!-- 功能卡片 -->
        <div class="feature-cards">
          <!-- 视频上传卡片 -->
          <div class="feature-card upload-card">
            <div class="card-header">
              <h3>🎬 视频上传</h3>
            </div>
            <div class="card-content">
              <p>上传视频到你的 TikTok 账户</p>
              <div class="upload-features">
                <div class="feature-item">
                  <span class="feature-icon">📤</span>
                  <span>支持多种视频格式</span>
                </div>
                <div class="feature-item">
                  <span class="feature-icon">⚙️</span>
                  <span>丰富的隐私设置</span>
                </div>
                <div class="feature-item">
                  <span class="feature-icon">📊</span>
                  <span>实时上传进度</span>
                </div>
              </div>
              <router-link to="/upload" class="btn btn-primary btn-upload">
                <span>🚀</span>
                开始上传视频
              </router-link>
            </div>
          </div>

          <!-- API 测试卡片 -->
          <div class="feature-card">
            <div class="card-header">
              <h3>🧪 API 测试</h3>
            </div>
            <div class="card-content">
              <p>测试各种 TikTok API 功能</p>
              <div class="api-actions">
                <button @click="testGetUserInfo" :disabled="state.loading" class="btn btn-small">
                  获取用户信息
                </button>
                <button @click="testHealthCheck" :disabled="state.loading" class="btn btn-small">
                  健康检查
                </button>
              </div>
            </div>
          </div>

          <!-- 令牌信息卡片 -->
          <div class="feature-card">
            <div class="card-header">
              <h3>🔐 令牌信息</h3>
            </div>
            <div class="card-content">
              <div class="token-info">
                <div class="info-item">
                  <span class="label">访问令牌:</span>
                  <span class="value">{{ maskedAccessToken }}</span>
                </div>
                <div class="info-item">
                  <span class="label">刷新令牌:</span>
                  <span class="value">{{ maskedRefreshToken }}</span>
                </div>
                <div class="info-item">
                  <span class="label">令牌状态:</span>
                  <span :class="['status', tokenStatus.class]">
                    {{ tokenStatus.text }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- 会话信息卡片 -->
          <div class="feature-card">
            <div class="card-header">
              <h3>📊 会话信息</h3>
            </div>
            <div class="card-content">
              <div class="session-info">
                <div class="info-item">
                  <span class="label">登录时间:</span>
                  <span class="value">{{ loginTime }}</span>
                </div>
                <div class="info-item">
                  <span class="label">服务状态:</span>
                  <span :class="['status', healthStatusClass]">
                    {{ healthStatusText }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 错误消息 -->
        <div v-if="getters.hasError.value" class="error-message">
          <div class="error-content">
            <span class="error-icon">⚠️</span>
            <span class="error-text">{{ state.error }}</span>
            <button @click="actions.clearError" class="error-close">✕</button>
          </div>
        </div>

        <!-- API 响应区域 -->
        <div v-if="apiResponse" class="api-response">
          <h3>API 响应</h3>
          <pre>{{ JSON.stringify(apiResponse, null, 2) }}</pre>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '../store'
import { TikTokAPI, TokenManager } from '../api/tiktok'

export default {
  name: 'Dashboard',
  setup() {
    const router = useRouter()
    const { state, getters, actions } = useAuthStore()
    const apiResponse = ref(null)

    // 计算属性
    const maskedAccessToken = computed(() => {
      const token = TokenManager.getAccessToken()
      if (!token) return '未获取'
      return token.substring(0, 10) + '...' + token.substring(token.length - 10)
    })

    const maskedRefreshToken = computed(() => {
      const token = TokenManager.getRefreshToken()
      if (!token) return '未获取'
      return token.substring(0, 10) + '...' + token.substring(token.length - 10)
    })

    const tokenStatus = computed(() => {
      if (!TokenManager.getAccessToken()) {
        return { text: '无令牌', class: 'status-error' }
      }
      if (TokenManager.isTokenExpired()) {
        return { text: '已过期', class: 'status-error' }
      }
      return { text: '有效', class: 'status-success' }
    })

    const loginTime = computed(() => {
      const userInfo = TokenManager.getUserInfo()
      if (userInfo && userInfo.loginTime) {
        return new Date(userInfo.loginTime).toLocaleString()
      }
      return '未知'
    })

    const healthStatusClass = computed(() => {
      if (!state.healthStatus) return 'status-unknown'
      return state.healthStatus.status === 'healthy' ? 'status-success' : 'status-error'
    })

    const healthStatusText = computed(() => {
      if (!state.healthStatus) return '检查中...'
      return state.healthStatus.status === 'healthy' ? '正常' : '异常'
    })

    // 方法
    const handleLogout = async () => {
      if (confirm('确定要退出登录吗？')) {
        actions.logout()
        router.push('/')
      }
    }

    const refreshUserInfo = async () => {
      try {
        await actions.fetchUserInfo()
        apiResponse.value = { action: 'refresh_user_info', result: '成功' }
      } catch (error) {
        console.error('刷新用户信息失败:', error)
      }
    }

    const refreshToken = async () => {
      try {
        await actions.refreshToken()
        apiResponse.value = { action: 'refresh_token', result: '成功' }
      } catch (error) {
        console.error('刷新令牌失败:', error)
      }
    }

    const testGetUserInfo = async () => {
      try {
        const result = await TikTokAPI.getUserInfo()
        apiResponse.value = result
      } catch (error) {
        apiResponse.value = { error: error.message }
      }
    }

    const testHealthCheck = async () => {
      try {
        const result = await TikTokAPI.checkHealth()
        apiResponse.value = result
        if (result.success) {
          state.healthStatus = result.data
        }
      } catch (error) {
        apiResponse.value = { error: error.message }
      }
    }

    // 初始化
    onMounted(async () => {
      await actions.initialize()
      await actions.checkHealth()
      
      // 记录登录时间
      const userInfo = TokenManager.getUserInfo()
      if (userInfo && !userInfo.loginTime) {
        userInfo.loginTime = new Date().toISOString()
        TokenManager.saveUserInfo(userInfo)
      }
    })

    return {
      state,
      getters,
      actions,
      apiResponse,
      maskedAccessToken,
      maskedRefreshToken,
      tokenStatus,
      loginTime,
      healthStatusClass,
      healthStatusText,
      handleLogout,
      refreshUserInfo,
      refreshToken,
      testGetUserInfo,
      testHealthCheck
    }
  }
}
</script>

<style scoped>
.dashboard-container {
  min-height: 100vh;
  background: #f5f7fa;
}

.navbar {
  background: white;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 100;
}

.nav-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.nav-title {
  font-size: 1.5rem;
  color: #333;
  margin: 0;
}

.logout-btn {
  background: #f44336;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background 0.3s ease;
}

.logout-btn:hover {
  background: #d32f2f;
}

.main-content {
  padding: 2rem;
}

.content-wrapper {
  max-width: 1200px;
  margin: 0 auto;
}

.user-card {
  background: white;
  border-radius: 16px;
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
}

.user-header {
  display: flex;
  align-items: center;
  margin-bottom: 2rem;
}

.user-avatar {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  object-fit: cover;
  margin-right: 2rem;
  border: 4px solid #667eea;
}

.user-avatar-placeholder {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: #f0f0f0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  margin-right: 2rem;
  border: 4px solid #667eea;
}

.user-name {
  font-size: 2rem;
  color: #333;
  margin-bottom: 0.5rem;
}

.user-id, .user-union-id {
  color: #666;
  font-family: 'Courier New', monospace;
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
}

.user-actions {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
}

.feature-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
}

.feature-card {
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.card-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 1.5rem;
}

.card-header h3 {
  margin: 0;
  font-size: 1.2rem;
}

.card-content {
  padding: 1.5rem;
}

.api-actions {
  display: flex;
  gap: 0.75rem;
  margin-top: 1rem;
  flex-wrap: wrap;
}

.token-info, .session-info {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.info-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 6px;
}

.label {
  font-weight: 600;
  color: #333;
}

.value {
  font-family: 'Courier New', monospace;
  color: #666;
  font-size: 0.9rem;
}

.status {
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
}

.status-success {
  background: #e8f5e8;
  color: #4caf50;
}

.status-error {
  background: #ffebee;
  color: #f44336;
}

.status-unknown {
  background: #fff3e0;
  color: #ff9800;
}

.error-message {
  background: #ffebee;
  border: 1px solid #ffcdd2;
  border-radius: 8px;
  margin-bottom: 2rem;
}

.error-content {
  display: flex;
  align-items: center;
  padding: 1rem;
}

.error-icon {
  margin-right: 0.75rem;
  font-size: 1.2rem;
}

.error-text {
  flex: 1;
  color: #d32f2f;
}

.error-close {
  background: none;
  border: none;
  color: #999;
  cursor: pointer;
  padding: 0.25rem;
  font-size: 1.2rem;
}

.api-response {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
}

.api-response h3 {
  margin-top: 0;
  margin-bottom: 1rem;
  color: #333;
}

.api-response pre {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 6px;
  overflow-x: auto;
  font-size: 0.9rem;
  line-height: 1.4;
}

/* 按钮样式 */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  border: none;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  font-size: 1rem;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: #667eea;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background: #5a6fd8;
  transform: translateY(-2px);
}

.btn-outline {
  background: transparent;
  color: #667eea;
  border: 2px solid #667eea;
}

.btn-outline:hover:not(:disabled) {
  background: #667eea;
  color: white;
}

.btn-small {
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
}

.btn-upload {
  background: #4caf50;
  color: white;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border-radius: 8px;
  border: none;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  font-size: 1rem;
}

.btn-upload:hover:not(:disabled) {
  background: #388e3c;
  transform: translateY(-2px);
}

.upload-features {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 1.5rem;
}

.feature-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  color: #333;
  font-size: 0.95rem;
}

.feature-icon {
  font-size: 1.1rem;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .nav-content {
    padding: 1rem;
  }
  
  .nav-title {
    font-size: 1.2rem;
  }
  
  .main-content {
    padding: 1rem;
  }
  
  .user-header {
    flex-direction: column;
    text-align: center;
  }
  
  .user-avatar, .user-avatar-placeholder {
    margin-right: 0;
    margin-bottom: 1rem;
  }
  
  .user-actions {
    justify-content: center;
  }

  .feature-cards {
    grid-template-columns: 1fr;
  }

  .upload-card {
    grid-column: 1 / -1; /* Make upload card span full width */
  }
  
  .api-actions {
    flex-direction: column;
  }
  
  .info-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
}
</style> 